import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ModeStandbyOutlined, PersonOff, People } from "@mui/icons-material";

import { CardLayout, MTable } from "../v3";
import { ClientTableColumns } from "../../pages/client/constants";
import { CRMService } from "../../services";
import { EXPORT_CLIENT } from "../../constants";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const ClientList = ({ companyId = null, totalCount = 0, setTotalCount = () => {}, reloadData }) => {
  const [clients, setClients] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    filter: selectedFilter,
    setFilter: setSelectedFilter,
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "clients",
    defaults: { filter: true, sortModel: [{ field: "updatedAt", sort: "desc" }] },
    filterValues: [true, false, null],
  });
  const [loading, setLoading] = useState(true);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const hasExportClient = useHasPermission(EXPORT_CLIENT);

  const fetchData = useCallback(
    async (clientFilters = {}) => {
      setLoading(true);
      try {
        const response = await CRMService.getClientList({
          ...clientFilters,
          type: "INDIVIDUAL",
          includeBalance: true,
          ...(companyId !== null && { companyId }),
        });
        setClients(response?.data?.rows);
        setTotalCount(response?.data?.count);
      } catch (error) {
        toast("Failed to fetch client list", { type: "error" });
        console.error("🚀 ~ fetchData ~ error:", error);
      } finally {
        setLoading(false);
      }
    },
    [setTotalCount, companyId],
  );

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      active: selectedFilter,
      name: searchText,
    });
  }, [fetchData, sortModel, paginationState, selectedFilter, searchText, reloadData]);

  const handleFilterChange = (value) => {
    const filterValue = value === "null" ? null : JSON.parse(value);
    setSelectedFilter(filterValue);
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfTitle = pdfTitleMap[String(selectedFilter)] || "All";
      const pdfColumnHeaders = derivePdfColumns(ClientTableColumns);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing
          title={`Clients : ${pdfTitle}`}
          columnHeaders={pdfColumnHeaders}
          tableRows={printableRows}
          company={user?.company}
        />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: `Clients : ${pdfTitle}`,
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const onViewClick = (params, event) => {
    navigate(`/clients/${params.row.id}`);
  };

  const filterOptions = [
    { label: "All", value: "null", icon: <ModeStandbyOutlined /> },
    { label: "Active", value: true, icon: <People /> },
    {
      label: "Inactive",
      value: false,
      icon: <PersonOff />,
    },
  ];

  const pdfTitleMap = {
    null: "All",
    true: "Active",
    false: "Inactive",
  };

  const rows = clients?.map((client, index) => {
    return {
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      id: client?.id,
      name: client?.name,
      primaryPhone: client?.primaryPhone,
      balance: client?.account?.balance,
    };
  });

  return (
    <CardLayout>
      <MTable
        // Filter data
        selectedFilter={selectedFilter}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        showFilterButton
        // Search data
        placeholder="Search client"
        searchText={searchText}
        onSearchChange={handleSearchChange}
        showSearchBar
        // Export data
        showExportButton={hasExportClient}
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={ClientTableColumns}
        tableRows={rows}
        rowCount={totalCount || 0}
        loading={loading}
        sortModel={sortModel}
        onSortChange={setSortModel}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
        onRowClick={onViewClick}
      />
    </CardLayout>
  );
};
